import React, {useEffect, useState} from 'react';
import {
    Box,
    Grid,
    ListItem,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Typography,
} from '@material-ui/core';
import {
    MyAccordion,
    MyAccordionSummary,
    MyAccordionDetails,
} from '../Accordions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {NavLink} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {H6} from 'components/TypographyShortcuts';
import InfoModal from 'components/widgets/tooltips/InfoModal';
import {usePutApplicationChecklist} from 'hooks';
import sectionContent from 'components/widgets/checklists/ChecklistSectionContent';

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '20em',
            height: '15em',
        },
        [theme.breakpoints.up('md')]: {
            width: '40em',
            height: '30em',
        },
    },
    accordion: {
        justifyContent: 'stretch',
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    summary: {
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    checkbox: {
        color: theme.palette.primary.main,
    },
}));

const ChecklistSectionFees = ({
    sectionId,
    checkoffs,
    complete,
    setComplete,
}) => {
    const classes = useStyles();

    const {put: putApplication, result} = usePutApplicationChecklist();

    const {labelId, title, link, link_text, fields, infoModalContent} =
        sectionContent?.[sectionId];

    const pruneData = sourceOfTruth =>
        fields.reduce((object, item) => {
            return {
                ...object,
                [item]: sourceOfTruth?.application_checklist[item] || false,
            };
        }, {});
    const sourceOfTruth = result || checkoffs;
    const [prunedData, setPrunedData] = useState(pruneData(sourceOfTruth));
    const [value, setValue] = useState(
        checkoffs?.application_checklist?.[labelId]
    );

    //initial value prunedData[labelId]
    const handleRadioChange = event => {
        setValue(event.target.value);
        const newData = {
            ...prunedData,
            [labelId]: event.target.value,
        };
        setPrunedData(newData);
        putApplication(newData);
        //setText(event.target.value);
    };

    useEffect(() => {
        if (result) {
            if (result?.application_checklist?.[labelId]) {
                const thisSectionComplete =
                    result?.application_checklist?.[labelId];
                setComplete(thisSectionComplete);
            } else {
                const thisSectionComplete =
                    result?.application_checklist?.valid_fees_receipt;
                setComplete(thisSectionComplete);
            }
        } else {
            if (checkoffs?.application_checklist?.[labelId]) {
                const thisSectionComplete =
                    checkoffs?.application_checklist?.[labelId];
                setComplete(thisSectionComplete);
            } else {
                const thisSectionComplete =
                    checkoffs?.application_checklist?.valid_fees_receipt;
                setComplete(thisSectionComplete);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result, value]);

    // Handle the POST updates in case a checkbox isn't allowed to be checked
    useEffect(() => {
        if (result || checkoffs) {
            setPrunedData(pruneData(sourceOfTruth));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result, sourceOfTruth]);

    return (
        <MyAccordion
            className={`${classes.accordion} ${complete ? 'complete' : ''}`}
        >
            <MyAccordionSummary
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
                expandIcon={<ExpandMoreIcon />}
            >
                <Grid container className={classes.titleContainer}>
                    <Grid
                        item
                        style={{
                            display: 'flex',
                            justifyContent: 'left',
                        }}
                        xs={12}
                    >
                        {title ? (
                            <H6>
                                <strong>{title}</strong>
                            </H6>
                        ) : null}
                        <InfoModal content={infoModalContent} />
                    </Grid>
                </Grid>
            </MyAccordionSummary>
            <MyAccordionDetails
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
            >
                <ListItem key={labelId} role='button' dense button>
                    <Grid container direction='column'>
                        <Grid item>
                            <FormControl component='fieldset'>
                                <Typography variant='body2'>
                                    Would you like MM-SEAS to take care of dealing
                                    with pay.gov fees for you?
                                </Typography>
                                <Box margin='1.5em 0'>
                                    <RadioGroup
                                        name='fees_help'
                                        onChange={handleRadioChange}
                                        defaultValue={JSON.stringify(value)}
                                    >
                                        <FormControlLabel
                                            value='true'
                                            control={<Radio />}
                                            label='Yes. MM-SEAS will cover all USCG Fees, and I will pay $299 for my perfect application.'
                                        />
                                        <Box marginTop='1.5em'>
                                            <FormControlLabel
                                                value='false'
                                                control={<Radio />}
                                                label='No. I covered the USCG fees myself and uploaded my receipt. I will pay $199 for my perfect application.'
                                            />
                                        </Box>
                                    </RadioGroup>
                                </Box>
                            </FormControl>
                        </Grid>

                        {prunedData[labelId] ? (
                            <Grid style={{marginTop: '1em'}}>
                                <Typography variant='body2'>
                                    We are happy to help and take care of this for
                                    you. We'll cover the fees and submit the
                                    application to the NMC and you'll be charged a
                                    flat amount of $299.
                                </Typography>
                            </Grid>
                        ) : (
                            <Grid>
                                {checkoffs?.application_checklist
                                    ?.valid_fees_receipt ? (
                                    <Grid>
                                        <Typography variant='body2'>
                                            You've already uploaded a receipt. Click
                                            the button if you'd like to upload
                                            another.
                                        </Typography>
                                        <p></p>
                                        <Button
                                            component={NavLink}
                                            to={link}
                                            variant='contained'
                                            color='secondary'
                                            style={{width: 'fit-content'}}
                                        >
                                            {link_text}
                                        </Button>
                                    </Grid>
                                ) : (
                                    <Grid>
                                        <Typography variant='body2'>
                                            Sounds great. Upload your receipt from
                                            pay.gov here.
                                        </Typography>
                                        <p></p>
                                        <Button
                                            component={NavLink}
                                            to={link}
                                            variant='contained'
                                            color='secondary'
                                            style={{width: 'fit-content'}}
                                        >
                                            {link_text}
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </ListItem>
            </MyAccordionDetails>
        </MyAccordion>
    );
};
export default ChecklistSectionFees;
